import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Skill import modal. User drops a ZIP or selects a folder of markdown files.
// Client-side validation runs first; on confirm, calls api.importSkill().
import { useRef, useState } from "react";
import JSZip from "jszip";
import { api, ApiClientError } from "@/lib/api";
import { log } from "@/lib/logger";
import { validateSkillStructure, skillTitleFromName } from "@/lib/skillMeta";
async function extractZip(file) {
    const zip = await JSZip.loadAsync(file);
    const entries = [];
    const promises = [];
    zip.forEach((relativePath, zipEntry) => {
        if (zipEntry.dir)
            return;
        if (relativePath.includes("__MACOSX/") || relativePath.endsWith(".DS_Store") || relativePath.split("/").some((part) => part.startsWith("._")))
            return;
        promises.push(zipEntry.async("text").then((content) => {
            entries.push({ path: relativePath, content });
        }));
    });
    await Promise.all(promises);
    return entries;
}
function stripTopFolder(files) {
    const parts = files.map((f) => f.path.split("/"));
    const topFolders = new Set(parts.map((p) => p[0]));
    if (topFolders.size !== 1)
        return files;
    const [top] = topFolders;
    if (!top)
        return files;
    // Only strip if every file has this top folder prefix
    const allHavePrefix = files.every((f) => f.path.startsWith(top + "/"));
    if (!allHavePrefix)
        return files;
    return files.map((f) => ({ ...f, path: f.path.slice(top.length + 1) })).filter((f) => f.path);
}
// Recursively reads a FileSystemDirectoryEntry into FileEntry[].
// This is the correct path for folder drops on Mac — dataTransfer.files is
// empty for directory drops but dataTransfer.items[i].webkitGetAsEntry()
// returns a FileSystemEntry that we can traverse.
async function readDirectoryEntry(dir, prefix = "") {
    const reader = dir.createReader();
    const allEntries = [];
    // readEntries() returns at most 100 at a time; call until exhausted.
    await new Promise((resolve, reject) => {
        function read() {
            reader.readEntries((batch) => {
                if (batch.length === 0) {
                    resolve();
                    return;
                }
                allEntries.push(...batch);
                read();
            }, reject);
        }
        read();
    });
    const results = [];
    for (const entry of allEntries) {
        if (entry.name.startsWith(".") || entry.name.startsWith("._"))
            continue;
        const fullPath = prefix ? `${prefix}/${entry.name}` : entry.name;
        if (entry.isDirectory) {
            results.push(...await readDirectoryEntry(entry, fullPath));
        }
        else {
            const file = await new Promise((res, rej) => entry.file(res, rej));
            results.push({ path: fullPath, content: await file.text() });
        }
    }
    return results;
}
export function SkillImportModal({ onClose, onImported }) {
    const fileRef = useRef(null);
    const [step, setStep] = useState("pick");
    const [files, setFiles] = useState([]);
    const [skillName, setSkillName] = useState("");
    const [errors, setErrors] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const [serverError, setServerError] = useState(null);
    const [importedSkill, setImportedSkill] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const dragCounterRef = useRef(0);
    function processEntries(raw) {
        const stripped = stripTopFolder(raw);
        const rawName = stripped.length > 0
            ? (stripped[0].path.split("/")[0] ?? "my-skill").replace(/[^A-Za-z0-9_-]/g, "-")
            : "my-skill";
        setSkillName(rawName);
        const validation = validateSkillStructure(stripped);
        setFiles(stripped);
        setErrors(validation.errors);
        setWarnings(validation.warnings);
        setStep("review");
    }
    function handleZipInput(zipFile) {
        const rawName = zipFile.name.replace(/\.zip$/i, "").replace(/[^A-Za-z0-9_-]/g, "-");
        setSkillName(rawName);
        extractZip(zipFile)
            .then((raw) => {
            const stripped = stripTopFolder(raw);
            const validation = validateSkillStructure(stripped);
            setFiles(stripped);
            setErrors(validation.errors);
            setWarnings(validation.warnings);
            setStep("review");
        })
            .catch((e) => {
            log.warn("skill-import-zip", { err: String(e) });
            setErrors(["Could not read ZIP file."]);
            setStep("review");
        });
    }
    function handleFileInput(e) {
        const picked = Array.from(e.target.files ?? []).filter((f) => !f.name.endsWith(".DS_Store") && !f.name.startsWith("._"));
        if (picked.length === 0)
            return;
        if (picked.length === 1 && picked[0].name.toLowerCase().endsWith(".zip")) {
            handleZipInput(picked[0]);
            return;
        }
        // Multiple individual files (e.g. folder select via webkitdirectory)
        const readers = picked.map((f) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve({
                path: f.webkitRelativePath || f.name,
                content: reader.result,
            });
            reader.onerror = reject;
            reader.readAsText(f);
        }));
        Promise.all(readers)
            .then(processEntries)
            .catch((e) => {
            log.warn("skill-import-files", { err: String(e) });
            setErrors(["Could not read selected files."]);
            setStep("review");
        });
    }
    // Drop handler — uses DataTransferItem.webkitGetAsEntry() so that folder
    // drops work on Mac (where dataTransfer.files is empty for directory drops).
    function handleDrop(e) {
        e.preventDefault();
        dragCounterRef.current = 0;
        setIsDragOver(false);
        const items = Array.from(e.dataTransfer.items ?? []);
        const firstItem = items[0];
        if (!firstItem)
            return;
        const entry = firstItem.webkitGetAsEntry?.();
        if (entry) {
            if (entry.isDirectory) {
                readDirectoryEntry(entry)
                    .then(processEntries)
                    .catch((err) => {
                    log.warn("skill-import-folder-drop", { err: String(err) });
                    setErrors(["Could not read dropped folder."]);
                    setStep("review");
                });
                return;
            }
            // File entry — get as File and check if ZIP
            entry.file((file) => {
                if (file.name.toLowerCase().endsWith(".zip"))
                    handleZipInput(file);
                else {
                    // Single non-zip file: wrap in array and process
                    file.text().then((content) => processEntries([{ path: file.name, content }]));
                }
            }, (err) => {
                log.warn("skill-import-file-entry", { err: String(err) });
                setErrors(["Could not read dropped file."]);
                setStep("review");
            });
            return;
        }
        // Fallback for browsers without webkitGetAsEntry
        const f = e.dataTransfer.files[0];
        if (f)
            handleZipInput(f);
    }
    function onDragEnter(e) {
        e.preventDefault();
        dragCounterRef.current += 1;
        setIsDragOver(true);
    }
    function onDragLeave(e) {
        e.preventDefault();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
            dragCounterRef.current = 0;
            setIsDragOver(false);
        }
    }
    async function handleImport() {
        setServerError(null);
        setStep("importing");
        try {
            const resp = await api.importSkill({ name: skillName, files });
            setImportedSkill(resp.skill);
            setWarnings(resp.warnings);
            setStep("done");
            onImported(resp.skill);
        }
        catch (err) {
            const msg = err instanceof ApiClientError ? err.message : "Import failed.";
            setServerError(msg);
            setStep("review");
        }
    }
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4", children: _jsxs("div", { className: "w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900", children: [_jsxs("div", { className: "mb-4 flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-semibold", children: "Import Skill" }), _jsx("button", { onClick: onClose, className: "rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800", "aria-label": "Close", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M18 6L6 18M6 6l12 12", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) }) })] }), step === "pick" && (_jsxs("div", { className: "space-y-4", children: [_jsxs("p", { className: "text-sm text-slate-600 dark:text-slate-400", children: ["Drop a ZIP or folder containing your skill files. Must include a", " ", _jsx("code", { className: "rounded bg-slate-100 px-1 dark:bg-slate-800", children: "SKILL.md" }), " ", "entry file with YAML frontmatter."] }), _jsxs("div", { className: `flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors ${isDragOver
                                ? "border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-950/20"
                                : "border-slate-300 dark:border-slate-600"}`, onDragEnter: onDragEnter, onDragLeave: onDragLeave, onDragOver: (e) => e.preventDefault(), onDrop: handleDrop, children: [_jsx("svg", { width: "40", height: "40", viewBox: "0 0 24 24", fill: "none", className: "text-slate-400", "aria-hidden": true, children: _jsx("path", { d: "M9 17H7a5 5 0 010-10h.5M15 17h2a5 5 0 000-10h-.5M12 12V3m0 0l-3 3m3-3l3 3", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }), _jsx("span", { className: "text-sm text-slate-500", children: isDragOver ? "Drop to import" : "Drag & drop a ZIP or folder here, or" }), !isDragOver && (_jsx("button", { className: "rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700", onClick: () => fileRef.current?.click(), children: "Choose files" })), _jsx("input", { ref: fileRef, type: "file", accept: ".zip,.md,.markdown,.txt", multiple: true, className: "hidden", onChange: handleFileInput })] })] })), (step === "review" || step === "importing") && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "mb-1 block text-sm font-medium", children: "Skill name (slug)" }), _jsx("input", { type: "text", className: "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800", value: skillName, onChange: (e) => setSkillName(e.target.value) }), skillName && (_jsxs("p", { className: "mt-1 text-xs text-slate-500", children: ["Will be displayed as: ", _jsx("strong", { children: skillTitleFromName(skillName) })] }))] }), _jsxs("div", { children: [_jsxs("p", { className: "mb-1 text-sm font-medium", children: ["Files (", files.length, ")"] }), _jsx("ul", { className: "max-h-36 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs dark:border-slate-700 dark:bg-slate-800", children: files.map((f) => (_jsx("li", { className: "py-0.5 font-mono", children: f.path }, f.path))) })] }), errors.length > 0 && (_jsxs("div", { className: "rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-900/50 dark:bg-rose-950/30", children: [_jsx("p", { className: "mb-1 text-sm font-medium text-rose-700 dark:text-rose-400", children: "Validation errors" }), _jsx("ul", { className: "space-y-0.5 text-xs text-rose-600 dark:text-rose-400", children: errors.map((e, i) => (_jsxs("li", { children: ["\u2022 ", e] }, i))) })] })), warnings.length > 0 && (_jsxs("div", { className: "rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30", children: [_jsx("p", { className: "mb-1 text-sm font-medium text-amber-700 dark:text-amber-400", children: "Warnings" }), _jsx("ul", { className: "space-y-0.5 text-xs text-amber-600 dark:text-amber-400", children: warnings.map((w, i) => (_jsxs("li", { children: ["\u2022 ", w] }, i))) })] })), serverError && (_jsx("p", { className: "text-sm text-rose-600 dark:text-rose-400", children: serverError })), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { className: "flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-800", onClick: () => { setFiles([]); setErrors([]); setWarnings([]); setStep("pick"); }, disabled: step === "importing", children: "Back" }), _jsx("button", { className: "flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60", onClick: handleImport, disabled: errors.length > 0 || step === "importing" || !skillName.trim(), children: step === "importing" ? "Importing…" : "Import Skill" })] })] })), step === "done" && importedSkill && (_jsxs("div", { className: "space-y-4 text-center", children: [_jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", className: "mx-auto text-emerald-500", "aria-hidden": true, children: [_jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2" }), _jsx("path", { d: "M8 12l2.5 2.5L16 9", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }), _jsxs("p", { className: "font-medium", children: ["\u201C", importedSkill.title, "\u201D imported successfully."] }), warnings.length > 0 && (_jsx("ul", { className: "text-left text-xs text-amber-600 dark:text-amber-400", children: warnings.map((w, i) => (_jsxs("li", { children: ["\u2022 ", w] }, i))) })), _jsx("button", { className: "w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700", onClick: onClose, children: "Done" })] }))] }) }));
}
